<template>
	<div class="d-flex justify-content-end w-100 px-3">
		<input
			v-model="query"
			class="form-control form-control-sm"
			style="width: 150px"
			placeholder="Search"
			type="text"
		/>
	</div>
	<div class="table-responsive p-3">
		<table class="table align-items-center mb-0 bordered-table">
			<thead>
				<tr>
					<th
						v-for="(column, index) in columns"
						:key="index"
						class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
					>
						{{ column.title }}
					</th>
					<th
						v-if="actions.length > 0"
						class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
					></th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="(row, index) in rows" :key="index">
					<td v-for="(column, index) in columns" :key="index">
						<div v-if="column.type == 'link'" class="d-flex px-2 py-1">
							<a :href="row[column.data]">{{ row[column.data] }}</a>
						</div>
						<div v-if="column.type == 'text'" class="d-flex px-2 py-1">
							<span v-if="!column.link">{{
								getValue(row, column.data, column)
							}}</span>
							<router-link
								v-if="column.link"
								:to="column.link.replace(':id', row.id)"
								>{{ getValue(row, column.data, column) }}</router-link
							>
						</div>
						<div v-if="column.type == 'date'" class="d-flex px-2 py-1">
							{{ $moment(row[column.data]).format('L') }}
						</div>
            <div v-if="column.type == 'time'" class="d-flex px-2 py-1">
							{{ $moment(row[column.data]).format('HH:mm') }}
						</div>
						<div v-if="column.type == 'button-link'" class="d-flex px-2 py-1">
							<router-link
								v-if="column.link"
								:to="column.link.replace(':id', row.id)"
								><soft-button variant="gradient" color="primary" full-width>{{
									column.text
								}}</soft-button></router-link
							>
						</div>
						<div v-if="column.type == 'boolean'" class="d-flex px-2 py-1">
							<soft-badge v-if="row[column.data] == true" color="success">{{
								row[column.data] ? column.on_label : column.off_label
							}}</soft-badge>
							<soft-badge v-if="row[column.data] == false" color="secondary">{{
								row[column.data] ? column.on_label : column.off_label
							}}</soft-badge>
						</div>
						<div v-if="column.type == 'horse-avatar'" class="d-flex px-2 py-1">
							<router-link
								v-if="column.link"
								:to="column.link.replace(':id', row.id)"
							>
								<soft-avatar
									:img="getHorseImage(row[column.data])"
									size="xxl"
									border-radius="sm"
									class="m-3"
									alt=""
								/>
							</router-link>
							<soft-avatar
								v-else
								:img="getHorseImage(row[column.data])"
								size="xxl"
								border-radius="sm"
								class="m-3"
								alt=""
							/>
						</div>
						<!-- MEMBER  -->
						<div v-if="column.type == 'member-avatar'" class="d-flex px-2 py-1">
							<div>
								<soft-avatar
									:img="row.photo_url"
									size="sm"
									border-radius="lg"
									class="me-3"
									alt="user1"
								/>
							</div>
							<div class="d-flex flex-column justify-content-center">
								<h6 class="mb-0 text-sm">
									{{ row.first_name ?? '' }}
									{{ row.last_name ?? '' }}
								</h6>
								<p class="text-xs text-secondary mb-0">
									{{ row.email ?? '' }}
								</p>
							</div>
						</div>
						<!-- // MEMBER  -->
						<!-- MEMBER CARD  -->
						<div
							v-if="column.type == 'member-card'"
							class="d-flex flex-column px-2 py-1"
						>
							<p class="text-xs font-weight-bold mb-0">
								{{ row.username }}
							</p>
							<p class="text-xs text-secondary mb-0">
								{{ row.id }}
							</p>
						</div>
						<!-- // MEMBER CARD  -->
						<div v-if="column.type == 'badge'" class="d-flex px-2 py-1">
							<soft-badge
								:color="column.color ?? 'success'"
								variant="gradient"
								size="sm"
								>{{ row[column.data] }}</soft-badge
							>
						</div>
					</td>
					<td v-if="actions.length > 0">
						<div class="d-flex px-2 py-1 justify-content-end">
							<a
								:class="{ loading: loading }"
								v-for="(action, index) in actions"
								:key="index"
								href="javascript:;"
								class="text-secondary font-weight-bold text-xs p-2"
								data-toggle="tooltip"
								@click="run(action, row)"
								:data-original-title="action.tooltip"
							>
								{{ action.title }}
							</a>
						</div>
					</td>
				</tr>
				<tr v-if="rows.length == 0">
					<td
						:colspan="columns.length"
						style="text-align: center; padding: 1em"
					>
						-- NO DATA --
					</td>
				</tr>
			</tbody>
		</table>
		<soft-pagination color="success" v-if="pagination.totalPages > 1">
			<soft-pagination-item v-if="pagination.page > 1" prev @click="prev()" />
			<soft-pagination-item
				@click="go(p)"
				v-for="(p, index) in getVisiblePages(
					pagination.page,
					pagination.totalPages
				)"
				:key="index"
				:label="p.toString()"
				:active="p == page"
			/>
			<soft-pagination-item
				v-if="pagination.page < pagination.totalPages"
				next
				@click="next()"
			/>
		</soft-pagination>
	</div>
</template>

<script>
	import SoftAvatar from '@/components/SoftAvatar.vue';
	import SoftButton from '@/components/SoftButton.vue';
	import SoftBadge from '@/components/SoftBadge.vue';
	import SoftPagination from '@/components/SoftPagination.vue';
	import SoftPaginationItem from '@/components/SoftPaginationItem.vue';
	import debounce from 'lodash.debounce';
	import { horseImagesService } from '@/services/HorseImagesService';

	export default {
		name: 'custom-table',
		props: {
			rows: {
				type: Array,
				default: () => [], // La prop che accetta i dati passati dall'esterno
			},
			columns: {
				type: Array,
				default: () => [],
			},
			actions: {
				type: Array,
				default: () => [],
			},
			params: {
				type: Object,
				default: () => {},
			},
			pagination: {
				type: Object,
				default: () => ({ page: 1, pageSize: 10, totalPages: 1 }),
			},
		},
		data() {
			return {
				loading: false,
				render: 0,
				page: this.pagination.page,
				total: this.pagination.totalPages,
				length: this.pagination.pageSize,
				query: '',
			};
		},
		watch: {
			query: debounce(function (search) {
				this.page = 1; // Ripristina la pagina iniziale quando si effettua una nuova ricerca
				this.$emit('search-changed', search); // Emetti l'evento per informare il componente padre del nuovo valore di ricerca
			}, 300),
		},
		created() {
			console.log('URL', this.columns);
			this.$emit('page-changed', this.page);
		},
		components: {
			SoftAvatar,
			SoftBadge,
			SoftPagination,
			SoftPaginationItem,
			SoftButton,
		},
		methods: {
			go(p) {
				this.page = p;
				this.$emit('page-changed', p); // Emissione dell'evento quando la pagina cambia
			},
			next() {
				if (this.page < this.total) {
					this.page++;
					this.$emit('page-changed', this.page);
				}
			},
			prev() {
				if (this.page > 1) {
					this.page--;
					this.$emit('page-changed', this.page);
				}
			},
			getVisiblePages(currentPage, totalPages) {
				const visiblePages = [];
				const maxVisible = 6;

				let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
				let end = Math.min(
					totalPages,
					currentPage + Math.floor(maxVisible / 2)
				);

				if (end - start + 1 < maxVisible) {
					if (start === 1) {
						end = Math.min(totalPages, start + maxVisible - 1);
					} else if (end === totalPages) {
						start = Math.max(1, end - maxVisible + 1);
					}
				}

				for (let i = start; i <= end; i++) {
					visiblePages.push(i);
				}

				return visiblePages;
			},
			getValue(obj, path, column) {
				if (column.render) return column.render(obj, column);
				return path.split('.').reduce((currentObject, key) => {
					return currentObject && currentObject[key] !== undefined
						? currentObject[key]
						: undefined;
				}, obj);
			},
			run(action, row) {
				this.$emit('action', { action, row });
			},
			getHorseImage(images) {
				return horseImagesService.getImage(images);
			},
		},
	};
</script>

<style>
	.pagination {
		justify-content: end;
		padding-top: 10px;
	}
</style>
